import axios from "axios";

const CUSTOMER_SESSION_KEY = "applyFlowCustomerSession";

const readStoredSession = () => {
  try {
    const raw =
      localStorage.getItem(CUSTOMER_SESSION_KEY) ||
      sessionStorage.getItem(CUSTOMER_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  } 
};

const writeStoredSession = (payload) => {
  const serialized = JSON.stringify(payload);
  localStorage.setItem(CUSTOMER_SESSION_KEY, serialized);
  sessionStorage.setItem(CUSTOMER_SESSION_KEY, serialized);
};

/** Backend loginCustomerMobile.php — only 3 steps */
export const STEP_TO_SCREEN = {
  1: "location",
  2: "details",
  3: "employment",
};

export const screenForStep = (step) => {
  const n = Number(step) || 1;
  return STEP_TO_SCREEN[n] || STEP_TO_SCREEN[1];
};

/** UI screen → backend step (max 3) */
export const backendStepForScreen = (screen) => {
  if (screen === "location") return 1;
  if (screen === "details") return 2;
  if (
    screen === "employment" ||
    screen === "processing" ||
    screen === "approved" ||
    screen === "offer" ||
    screen === "processBank" ||
    screen === "selectBank" ||
    screen === "connectBank" ||
    screen === "aaAccount" ||
    screen === "addBankDetails" ||
    screen === "aadhaarKyc" ||
    screen === "referenceDetails" ||
    screen === "referenceReview" ||
    screen === "selfieIntro" ||
    screen === "selfieCamera" ||
    screen === "selfieCapture" ||
    screen === "leadSubmitting" ||
    screen === "leadSubmitFail" ||
    screen === "selfieSuccess" ||
    screen === "profile" ||
    screen === "paymentHistory" ||
    screen === "hold"
  ) {
    return 3;
  }
  return null;
};

export const stepForScreen = (screen) => backendStepForScreen(screen);

/** Apply-new flow order — resume ke liye kaun sa screen aage hai */
export const FLOW_PROGRESS_SCREENS = [
  "location",
  "details",
  "employment",
  "processing",
  "approved",
  "offer",
  "processBank",
  "selectBank",
  "connectBank",
  "aaMobile",
  "aaAccount",
  "aaConsent",
  "addBankDetails",
  "hold",
  "aadhaarKyc",
  "referenceDetails",
  "referenceReview",
  "selfieIntro",
  "selfieCamera",
  "selfieCapture",
  "leadSubmitting",
  "leadSubmitFail",
  "selfieSuccess",
  "profile",
  "paymentHistory",
];

export const screenProgressIndex = (screen) => {
  const idx = FLOW_PROGRESS_SCREENS.indexOf(screen);
  return idx >= 0 ? idx : -1;
};

export const pickFurthestScreen = (a, b) => {
  if (!a) return b || "location";
  if (!b) return a;
  return screenProgressIndex(a) >= screenProgressIndex(b) ? a : b;
};

export const mergeCustomerSession = (patch) => {
  const prev = getCustomerSession() || {};
  saveCustomerSession({ ...prev, ...patch });
};

export const saveCustomerSession = (payload) => {
  if (!payload || typeof payload !== "object") return;
  writeStoredSession(payload);
};

export const getCustomerSession = () => readStoredSession();

export const clearCustomerSession = () => {
  localStorage.removeItem(CUSTOMER_SESSION_KEY);
  sessionStorage.removeItem(CUSTOMER_SESSION_KEY);
};

const normalizeSavedFields = (savedRaw) => {
  let savedFields = {};
  if (Array.isArray(savedRaw)) {
    savedRaw.forEach((item) => {
      if (item && typeof item === "object") Object.assign(savedFields, item);
    });
  } else if (savedRaw && typeof savedRaw === "object") {
    savedFields = { ...savedRaw };
  }
  return savedFields;
};

const PROGRESS_FIELD_KEYS = [
  "pan",
  "email",
  "latitude",
  "longitude",
  "coordinates",
  "employer",
  "employer_name",
  "monthly_income",
  "salary",
  "salary_date",
  "loan_amount",
  "loan_purpose",
  "employment_type",
  "salary_mode",
  "aadhaar_number",
  "aadhaar_verified",
];

const extractSavedFields = (savedRaw, ...sources) => {
  const savedFields = normalizeSavedFields(savedRaw);
  sources.forEach((source) => {
    if (!source || typeof source !== "object") return;
    PROGRESS_FIELD_KEYS.forEach((key) => {
      const value = source[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        savedFields[key] = value;
      }
    });
  });
  return savedFields;
};

/** Backend kabhi step:1 bhej deta hai jab data saved ho — fields se step nikalo */
export const inferStepFromSavedFields = (savedFields = {}) => {
  const fields = savedFields || {};
  const hasEmployment =
    fields.employer ||
    fields.employer_name ||
    fields.monthly_income ||
    fields.salary ||
    fields.loan_amount;
  const hasPanEmail = fields.pan || fields.email;
  const hasLocation =
    fields.latitude || fields.longitude || fields.coordinates;

  if (hasEmployment) return 3;
  if (hasPanEmail) return 3;
  if (hasLocation) return 2;
  return 1;
};

/** Pick resume screen from backend step + saved data */
export const resolveResumeScreen = (step, savedFields = {}) => {
  const mergedStep = Math.min(
    Math.max(
      Math.max(Number(step) || 1, inferStepFromSavedFields(savedFields)),
      1
    ),
    3
  );
  const fields = savedFields || {};
  const storedLastScreen =
    typeof fields.last_screen === "string" ? fields.last_screen : "";

  if (storedLastScreen && screenProgressIndex(storedLastScreen) >= 0) {
    return storedLastScreen;
  }

  if (fields.aadhaar_verified === "1" || fields.aadhaar_verified === true) {
    return "referenceDetails";
  }

  if (mergedStep >= 3) {
    const hasEmployment =
      fields.employer ||
      fields.employer_name ||
      fields.monthly_income ||
      fields.salary ||
      fields.loan_amount;
    if (hasEmployment) return "processBank";
    return "employment";
  }

  if (mergedStep >= 2) return "details";
  return "location";
};

export const buildSessionFromStored = (stored) => {
  if (!stored || typeof stored !== "object") return null;

  const savedFields = extractSavedFields(stored.saved_fields ?? {}, stored);
  const step = Math.min(
    Math.max(Number(stored.step) || 1, inferStepFromSavedFields(savedFields)),
    3
  );
  const customerMobile = String(stored.customer_mobile || "")
    .replace(/\D/g, "")
    .slice(-10);
  if (customerMobile.length !== 10) return null;

  const lastScreen =
    stored.last_screen || resolveResumeScreen(step, savedFields);

  return {
    ok: true,
    message: "",
    step,
    screen: lastScreen,
    customerMobile,
    savedFields,
    sessionData: {
      ...stored,
      step,
      customer_mobile: customerMobile,
      last_screen: lastScreen,
      saved_fields: savedFields,
    },
    leadId: stored.lead_id || stored.inserted_id || stored.customer_id || "",
  };
};

/** Prefer local last_screen — backend sirf 3 step track karta hai */
export const pickResumeScreen = (localSession, apiLogin) => {
  const localFields = extractSavedFields(localSession?.saved_fields ?? {}, localSession);
  const apiFields = extractSavedFields(
    apiLogin?.savedFields ?? {},
    apiLogin?.sessionData
  );
  const mergedFields = { ...localFields, ...apiFields };

  const localStep = Math.max(
    Number(localSession?.step) || 0,
    inferStepFromSavedFields(localFields)
  );
  const apiStep = Math.max(
    Number(apiLogin?.step) || 0,
    inferStepFromSavedFields(apiFields)
  );
  const mergedStep = Math.min(
    Math.max(localStep, apiStep, inferStepFromSavedFields(mergedFields)),
    3
  );

  const apiScreen = resolveResumeScreen(mergedStep, {
    ...mergedFields,
    last_screen: localSession?.last_screen,
  });
  return pickFurthestScreen(localSession?.last_screen, apiScreen);
};

export const buildCustomerSessionParams = (customerMobile, step, fields = {}) => {
  const params = new URLSearchParams();
  params.append(
    "customer_mobile",
    String(customerMobile || "").replace(/\D/g, "").slice(-10)
  );
  params.append("step", String(Math.min(Math.max(Number(step) || 1, 1), 3)));

  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.append(key, String(value));
    }
  });

  return params;
};

export const fetchCustomerSession = async (url, customerMobile) => {
  const mobile = String(customerMobile || "").replace(/\D/g, "").slice(-10);
  if (mobile.length !== 10) {
    return { ok: false, message: "Invalid mobile" };
  }

  const params = new URLSearchParams();
  params.append("customer_mobile", mobile);

  const resp = await axios.post(url, params, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    timeout: 20000,
    withCredentials: true,
    validateStatus: () => true,
  });

  return parseLoginCustomerResponse(resp.data);
};

export const postCustomerSession = async (url, customerMobile, step, fields = {}) => {
  const mobile = String(customerMobile || "").replace(/\D/g, "").slice(-10);
  if (mobile.length !== 10) {
    return { ok: false, message: "Invalid mobile" };
  }

  const params = buildCustomerSessionParams(mobile, step, fields);
  const resp = await axios.post(url, params, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    timeout: 20000,
    withCredentials: true,
    validateStatus: () => true,
  });

  return parseLoginCustomerResponse(resp.data);
};

/**
 * loginCustomerMobile.php — read + save (same endpoint).
 * POST customer_mobile → resume
 * POST customer_mobile + step + fields → save progress
 */
export const parseLoginCustomerResponse = (raw) => {
  const body = raw && typeof raw === "object" ? raw : {};
  const data = body.data && typeof body.data === "object" ? body.data : {};
  const session =
    data.session_data && typeof data.session_data === "object"
      ? data.session_data
      : {};

  const savedFields = extractSavedFields(
    data.saved_fields ?? session.saved_fields ?? {},
    session,
    data
  );

  const reportedStep = Number(data.step ?? session.step ?? 1) || 1;
  const step = Math.min(
    Math.max(reportedStep, inferStepFromSavedFields(savedFields)),
    3
  );
  const customerMobile = String(
    data.customer_mobile || session.customer_mobile || ""
  )
    .replace(/\D/g, "")
    .slice(-10);

  return {
    ok: body.status === true,
    message: String(body.message || ""),
    step,
    screen: resolveResumeScreen(step, savedFields),
    customerMobile,
    savedFields,
    sessionData: {
      ...session,
      ...data,
      step,
      customer_mobile: customerMobile,
      saved_fields: savedFields,
    },
    leadId:
      data.lead_id ||
      data.inserted_id ||
      data.customer_id ||
      session.lead_id ||
      session.inserted_id ||
      "",
  };
};
